import { LocationId } from "@redotech/redo-inventory-id";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useLocationZones } from "./use-location-zones";

export interface PickerZoneState {
  enabled: boolean;
  selectionRequired: boolean;
  zoneId: string | null;
  setZoneId: (id: string | null) => void;
  zones: { id: string; name: string }[];
  pending: boolean;
  error: unknown;
  blocked: boolean;
  retry: () => void;
}

const STORAGE_PREFIX = "pickerZoneId:";

function readInitialMap(): Record<string, string> {
  if (typeof localStorage === "undefined") {
    return {};
  }
  const out: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const v = localStorage.getItem(key);
        if (v) {
          out[key.slice(STORAGE_PREFIX.length)] = v;
        }
      }
    }
  } catch {
    // ignore
  }
  return out;
}

let store: Record<string, string> = readInitialMap();
const subscribers = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

function getSnapshot(): Record<string, string> {
  return store;
}

function setZoneFor(locationId: string, zoneId: string | null): void {
  if ((store[locationId] ?? null) === zoneId) {
    return;
  }
  const next = { ...store };
  if (zoneId == null) {
    delete next[locationId];
  } else {
    next[locationId] = zoneId;
  }
  store = next;
  try {
    if (zoneId == null) {
      localStorage.removeItem(`${STORAGE_PREFIX}${locationId}`);
    } else {
      localStorage.setItem(`${STORAGE_PREFIX}${locationId}`, zoneId);
    }
  } catch {
    // ignore
  }
  for (const l of subscribers) {
    l();
  }
}

const NOOP_SET_ZONE_ID = (_id: string | null): void => {};

export function usePickerZone(locationId: LocationId | null): PickerZoneState {
  const {
    enabled: zonesEnabled,
    selectionRequired,
    zones,
    pending,
    defaultZoneId,
    error,
    blocked,
    retry,
  } = useLocationZones(locationId);
  const map = useSyncExternalStore(subscribe, getSnapshot);

  const enabled = zonesEnabled && locationId != null;

  const zoneId = useMemo<string | null>(() => {
    if (!enabled || locationId == null) {
      return null;
    }
    const stored = map[locationId];
    if (stored && zones.some((z) => z.id === stored)) {
      return stored;
    }
    if (!selectionRequired) {
      return null;
    }
    if (defaultZoneId && zones.some((z) => z.id === defaultZoneId)) {
      return defaultZoneId;
    }
    return zones[0]?.id ?? null;
  }, [enabled, locationId, zones, map, selectionRequired, defaultZoneId]);

  const setZoneId = useCallback(
    (id: string | null) => {
      if (locationId == null) {
        return;
      }
      setZoneFor(locationId, id);
    },
    [locationId],
  );

  return {
    enabled,
    selectionRequired,
    zoneId,
    setZoneId: locationId == null ? NOOP_SET_ZONE_ID : setZoneId,
    zones,
    pending,
    error,
    blocked,
    retry,
  };
}
